import type { ReactNode } from 'react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';

// 02 PAIN — Wave 4.1 (Igor 2026-06-04, ref `06-awesomic-rounded-minimalism.md`):
// "Dark Problem Panel" pattern from Awesomic — 28px rounded dark cards
// with inline weight-contrast text. Lead-in phrasing renders in light weight
// muted; the key noun/verb in each card carries the message in semibold
// primary. Igor 2026-06-04: panels are now translucent cool pressed-glass
// (faint blue→cyan sheen + backdrop-blur over the atmosphere) so they don't
// read as flat #444 slabs; and the accent cyan spans the whole «ИИ и
// вайбкодинг» phrase, not just «вайбкодинг». Inset top hairline simulates
// Awesomic's `--shadow-subtle-3` inner-highlight on our dark surface.
//
// Texts: README.md §5 (byte-equal, including author typos). The body split
// into spans is purely typographic emphasis — no copy invented, no order
// changed; commas / periods stay attached to their original phrases.

export const meta: ModuleMeta = {
  id: '02-pain',
  title: 'БОЛЬ РЫНКА',
  status: 'approved',
  viewport: '390x844',
};

interface PainCardData {
  /** Question chip rendered as a small dark filled badge. */
  question: string;
  /** Body with inline `<strong>` weight-contrast spans. */
  body: ReactNode;
}

const CARDS: PainCardData[] = [
  {
    question: 'Что хотят клубы?',
    body: (
      <>
        <span className="module-02-lead">Футбольные клубы хотят </span>
        <strong className="module-02-key">интерактивный диджитал</strong>
        <span className="module-02-lead">
          : геймификацию, мини-приложения, вовлекающие механики для болельщиков
        </span>
      </>
    ),
  },
  {
    question: 'Что они получают?',
    body: (
      <>
        <span className="module-02-lead">Но рынок разработки к ним </span>
        <strong className="module-02-key">жесток</strong>
        <span className="module-02-lead">
          . Средний проект стоит от{' '}
        </span>
        <strong className="module-02-key">500к до 1,5 млн рублей</strong>
        <span className="module-02-lead">
          , делается от 1–2 месяцев, а подрядчики не понимают ни футбол, ни
          болельщика. Хорошие идеи остаются на бумаге
        </span>
      </>
    ),
  },
  {
    question: 'Кто сломал правила?',
    body: (
      <>
        <strong className="module-02-key vibe">ИИ и вайбкодинг</strong>
        <span className="module-02-lead">
          {' '}сломали старую экономику разработки. То, что раньше стоило 1м и
          делалось 2 месяца — сегодня можно сделать в{' '}
        </span>
        <strong className="module-02-key">5 раз дешевле и быстрее</strong>
        <span className="module-02-lead"> без потери качества</span>
      </>
    ),
  },
];

export default function PainModule() {
  return (
    <section
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={1} shafts={0} intensity="calm" />

      <div
        className="relative z-10 flex min-h-full flex-col px-sp-5"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 24px)' }}
      >
        <div className="flex flex-col gap-sp-4 pb-sp-10">
          {CARDS.map((card, i) => (
            <article
              key={card.question}
              className={`module-02-card module-02-card--stagger-${i + 1} motion-slide-in motion-stagger-${i + 1}`}
            >
              <span className="module-02-chip">{card.question}</span>
              <p className="module-02-body">{card.body}</p>
            </article>
          ))}
        </div>
      </div>

      {/* Universal swipe-up affordance. */}
      <div
        className="hint-arrow-pulse pointer-events-none absolute bottom-sp-7 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-sp-3 text-text-muted"
        aria-hidden
      >
        <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
        <span className="text-[1.25rem] leading-none">↓</span>
      </div>
    </section>
  );
}
